import { useEffect, useState } from "react";
import { useDashboard } from "../../../lib/useDashboard";
import { getInitialBoolean } from "@/shared/lib/localStorage";
import { MainTypesPlans } from "../../../model/dashboard.types";
import { useDashboardActions } from "../../../lib/useDashboardActions";
import { useAuth } from "@/features/Auth/lib/useAuth";
import { CompanyResult } from "../../../model/dashboard.types";
import { DEPARTMENTS_PLAN_IDS } from "../../../model/dashboard.types";
import { getFirstDayOfCurrentMonth, getLastDayOfCurrentMonth, toLocalDateString } from "@/shared/lib/dates";
import { canSeeCommercialOverview } from '@/features/Auth/lib/salesAccess';

export type DepartmentPlanKey = `${number}_${MainTypesPlans}`;

const IS_PERIOD_KEY = "useDepartmentsPlan_isPeriod";

export function useDepartmentsPlan() {
  const { dateStart, dateEnd } = useDashboard();
  const { getDepartmentsResult } = useDashboardActions();
  const { isAuth, user } = useAuth();

  const [isPeriod, setIsPeriod] = useState<boolean>(getInitialBoolean(IS_PERIOD_KEY, true));
  const [results, setResults] = useState<Record<DepartmentPlanKey, CompanyResult | null>>(
    {} as Record<DepartmentPlanKey, CompanyResult | null>
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const director = canSeeCommercialOverview(user);
  const start = dateStart ?? (director ? toLocalDateString(getFirstDayOfCurrentMonth()) : null);
  const end = dateEnd ?? (director ? toLocalDateString(getLastDayOfCurrentMonth()) : null);

  useEffect(() => {
    localStorage.setItem(IS_PERIOD_KEY, String(isPeriod));
  }, [isPeriod]);

  useEffect(() => {
    if (!isAuth || !start || !end) return;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      const newResults: Record<string, CompanyResult | null> = {};

      const promises = DEPARTMENTS_PLAN_IDS.flatMap((departmentId) =>
        ([MainTypesPlans.opportunitie, MainTypesPlans.profit] as const).map(
          async (type) => {
            try {
              const result = await getDepartmentsResult({
                dateStart: start,
                dateEnd: end,
                isPeriod,
                type,
                departmentId,
              });
              newResults[`${departmentId}_${type}`] = result;
            } catch {
              newResults[`${departmentId}_${type}`] = null;
            }
          }
        )
      );

      await Promise.all(promises);
      setResults(newResults as Record<DepartmentPlanKey, CompanyResult | null>);
      setLoading(false);
    };

    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, start, end, isPeriod]);

  return {
    results,
    loading,
    error,
    isPeriod,
    setIsPeriod,
    departmentIds: DEPARTMENTS_PLAN_IDS,
    dateStart: start,
    dateEnd: end,
  };
}
